import {
  getDocument,
  GlobalWorkerOptions,
  type PDFDocumentProxy,
} from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs' with { type: 'file' };
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { referenceDocumentCopyUrl } from '../lib/decks.ts';
import { pdfViewerUrl, readPdfLocation } from '../lib/pdf-location.ts';
import { Button } from '../components/ui/button.tsx';
import { Alert, AlertDescription } from '../components/ui/alert.tsx';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export function PdfPage() {
  const intl = useIntl();
  const location = readPdfLocation(new URL(window.location.href));
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [document, setDocument] = useState<PDFDocumentProxy | Error | null>(
    null,
  );
  const [page, setPage] = useState(location?.page ?? 1);

  useEffect(() => {
    if (!location) return;
    const loadingTask = getDocument({
      url: referenceDocumentCopyUrl(
        location.deckId,
        location.referenceDocumentId,
      ),
    });
    let active = true;
    void loadingTask.promise.then(
      (loaded) => {
        if (active) setDocument(loaded);
      },
      () => {
        if (active) setDocument(new Error('pdf'));
      },
    );
    return () => {
      active = false;
      void loadingTask.destroy();
    };
  }, [location?.referenceDocumentId, location?.deckId]);

  useEffect(() => {
    if (!location || !document || document instanceof Error) return;
    const boundedPage = Math.min(page, document.numPages);
    if (boundedPage !== page) {
      setPage(boundedPage);
      return;
    }
    let active = true;
    let renderTask: ReturnType<
      Awaited<ReturnType<typeof document.getPage>>['render']
    > | null = null;
    void document.getPage(page).then((pdfPage) => {
      const canvas = canvasRef.current;
      if (!active || !canvas) return;
      const viewport = pdfPage.getViewport({ scale: 1.5 });
      const context = canvas.getContext('2d');
      if (!context) return;
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      renderTask = pdfPage.render({ canvas, canvasContext: context, viewport });
      return renderTask.promise;
    });
    return () => {
      active = false;
      renderTask?.cancel();
    };
  }, [document, location, page]);

  if (!location)
    return (
      <section className="grid gap-4">
        <Alert variant="destructive">
          <AlertDescription>
            {intl.formatMessage({ id: 'web.pdf.incompleteAddress' })}
          </AlertDescription>
        </Alert>
      </section>
    );
  function goToPage(nextPage: number) {
    if (!document || document instanceof Error) return;
    const boundedPage = Math.max(1, Math.min(nextPage, document.numPages));
    setPage(boundedPage);
    window.history.replaceState(
      null,
      '',
      pdfViewerUrl({ ...location!, page: boundedPage }),
    );
  }
  return (
    <section className="grid gap-4">
      <header className="flex items-center justify-between gap-3">
        {document && !(document instanceof Error) ? (
          <div className="flex items-center gap-3">
            <Button
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              type="button"
              variant="outline"
            >
              {intl.formatMessage({ id: 'web.pdf.previous' })}
            </Button>
            <span>
              {intl.formatMessage(
                { id: 'web.pdf.pagePosition' },
                { page, pageCount: document.numPages },
              )}
            </span>
            <Button
              disabled={page >= document.numPages}
              onClick={() => goToPage(page + 1)}
              type="button"
              variant="outline"
            >
              {intl.formatMessage({ id: 'web.pdf.next' })}
            </Button>
          </div>
        ) : null}
      </header>
      {document instanceof Error ? (
        <Alert variant="destructive">
          <AlertDescription>
            {intl.formatMessage({ id: 'web.pdf.error' })}
          </AlertDescription>
        </Alert>
      ) : !document ? (
        <p className="text-muted-foreground">
          {intl.formatMessage({ id: 'web.pdf.loading' })}
        </p>
      ) : (
        <div className="max-h-[75vh] overflow-auto rounded-lg border border-border bg-secondary p-4">
          <canvas
            className="mx-auto block h-auto max-w-full bg-white shadow-[0_8px_18px_rgb(23_22_20_/_0.06)]"
            ref={canvasRef}
          />
        </div>
      )}
    </section>
  );
}
